import { QUALITY_MAX_PAGE_SIZE, QUALITY_SEARCH_MIN_LENGTH } from '@gozoki/api-types'
import z from 'zod/v3'

export const qualityStatuses = ['all', 'blocked', 'unblocked'] as const

export const QUALITY_SEARCH_DEBOUNCE_MS = 500

// Filtres communs aux deux onglets (lots et articles) : chaque onglet étend ce
// schéma avec ses propres filtres (n° de lot + statut, ou référence article).
export const qualitySharedSearchSchema = z.object({
  dateEndDlc: z.coerce.date().optional(),
  dateEndSold: z.coerce.date().optional(),
  dateStartDlc: z.coerce.date().optional(),
  dateStartSold: z.coerce.date().optional(),
  ean: z.string().optional(),
  label: z.string().optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  storeId: z.number().optional(),
})

export type QualitySharedSearch = z.infer<typeof qualitySharedSearchSchema>

/** Valeurs des filtres partagés, une fois les valeurs par défaut appliquées. */
export interface QualitySharedFilters {
  page: number
  pageSize: number
  ean: string | null
  label: string | null
  storeId: number | null
  dateStartSold?: Date
  dateEndSold?: Date
  dateStartDlc?: Date
  dateEndDlc?: Date
}

export const resolveQualitySharedFilters = (search: QualitySharedSearch): QualitySharedFilters => ({
  page: search.page ?? 0,
  // Une URL partagée peut porter n'importe quelle taille : ramenée au plafond de l'API (422 au-delà).
  pageSize: Math.min(search.pageSize ?? 20, QUALITY_MAX_PAGE_SIZE),
  ean: search.ean ?? null,
  label: search.label ?? null,
  storeId: search.storeId ?? null,
  dateStartSold: search.dateStartSold,
  dateEndSold: search.dateEndSold,
  dateStartDlc: search.dateStartDlc,
  dateEndDlc: search.dateEndDlc,
})

/** Filtres partagés remis sous forme de search params (changement d'onglet). */
export const toQualitySharedSearch = (filters: QualitySharedFilters): QualitySharedSearch => ({
  page: filters.page,
  pageSize: filters.pageSize,
  ean: filters.ean ?? undefined,
  label: filters.label ?? undefined,
  storeId: filters.storeId ?? undefined,
  dateStartSold: filters.dateStartSold,
  dateEndSold: filters.dateEndSold,
  dateStartDlc: filters.dateStartDlc,
  dateEndDlc: filters.dateEndDlc,
})

/**
 * Les requêtes lots/articles joignent des tables de plusieurs millions de lignes :
 * sans identifiant en entrée elles saturent la base, on ne les lance donc pas. Même règle que
 * l'API : un filtre d'au moins `QUALITY_SEARCH_MIN_LENGTH` caractères.
 * `tabSearch` est le filtre propre à l'onglet (n° de lot ou référence article).
 */
export const hasQualitySearchCriteria = (filters: QualitySharedFilters, tabSearch: string | null) =>
  [tabSearch, filters.ean, filters.label].some(
    (value) => (value ?? '').trim().length >= QUALITY_SEARCH_MIN_LENGTH,
  )

/** Partie commune de la query envoyée aux endpoints lots et articles. */
export const buildQualitySharedQuery = (filters: QualitySharedFilters) => ({
  page: filters.page + 1, // X-data-grid starts at 0 but lucid starts at 1
  pageSize: filters.pageSize,
  ean: filters.ean ?? undefined,
  label: filters.label ?? undefined,
  store: filters.storeId ?? undefined,
  dateStartSold: filters.dateStartSold?.toISOString(),
  dateEndSold: filters.dateEndSold?.toISOString(),
  dateStartDlc: filters.dateStartDlc?.toISOString(),
  dateEndDlc: filters.dateEndDlc?.toISOString(),
})
